import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaPlus } from 'react-icons/fa';
import { dashboardService } from '../../services/dashboardService';
import { agentsService } from '../../services/agentsService';
import { useAuth } from '../../context/AuthContext';
import AgentDot from '../../components/ui/AgentDot';
import StatusTag from '../../components/ui/StatusTag';
import { PALETTES, agentLookOf } from '../../utils/agentLook';
import { isExternal } from '../../utils/agentReadiness';
import { buildAttention, statusLine } from '../../utils/overview';
import AttentionCard from './AttentionCard';
import HealthCard from './HealthCard';
import AgentsOrigin from './AgentsOrigin';

const fmt = (n) => (n ?? 0).toLocaleString('es-MX');
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtShortDate = (s) => new Date(s + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
const isoDay = (offset) => { const d = new Date(Date.now() - offset * 86400000); return new Date(d.toLocaleString('en-US', { timeZone: 'America/Mexico_City' })).toLocaleDateString('en-CA'); };

const ACTION_LABELS = {
  block_user: 'bloqueó a', unblock_user: 'desbloqueó a', update_plan: 'cambió el plan de', reset_usage: 'reinició el uso de',
  update_limits: 'actualizó los límites de', create_user: 'creó a', delete_user: 'eliminó a',
  agent_publish: 'publicó un agente', credential_save: 'guardó una clave',
  agent_create: 'creó un agente', agent_credential: 'asignó una clave a un agente', credential_update: 'editó una clave',
  agent_restore: 'restauró un agente', agent_archive: 'archivó un agente', project_engine: 'cambió el motor de un proyecto', agent_managed_by: 'cambió dónde vive un agente',
};
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches'; };

const Card = ({ title, children, className = '', action }) => (
  <section className={`rounded-3xl bg-panel ring-1 ring-line p-5 sm:p-6 ${className}`} style={{ boxShadow: 'var(--shadow)' }} aria-label={title}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="font-display text-[18px] font-bold text-fg">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const ChartTip = ({ active, payload, label }) => (!active || !payload?.length ? null : (
  <div className="rounded-xl bg-panel ring-1 ring-line px-3 py-2 text-[12.5px]" style={{ boxShadow: 'var(--shadow)' }}>
    <p className="mb-1 font-semibold text-fg">{fmtShortDate(label)}</p>
    {payload.map((p) => <p key={p.dataKey} className="text-fg-dim"><span style={{ color: p.color }}>●</span> {p.name}: <b className="text-fg">{fmt(p.value)}</b></p>)}
  </div>
));

const DashboardPage = () => {
  const { currentUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [ovState, setOvState] = useState('loading');          // loading | ready | missing | error
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState({ open: 0, closed: 0, inProgress: 0 });
  const [log, setLog] = useState([]);
  const [agents, setAgents] = useState(null);

  useEffect(() => {
    dashboardService.getOverview()
      .then((o) => { setOverview(o); setOvState('ready'); })
      .catch((e) => { console.error('Overview load error:', e); setOvState(e.code === 'missing_43' ? 'missing' : 'error'); });
    Promise.allSettled([dashboardService.getStats(), dashboardService.getTicketStats(), dashboardService.getAdminLog()]).then(([s, t, l]) => {
      if (s.status === 'fulfilled') setStats(s.value);
      if (t.status === 'fulfilled') setTickets(t.value);
      if (l.status === 'fulfilled') setLog(l.value);
    });
    agentsService.listAgents().then((a) => setAgents(a.filter((x) => !isExternal(x) && x.status !== 'archived'))).catch(() => setAgents([]));
  }, []);

  const items = useMemo(() => buildAttention(overview), [overview]);
  const firstName = (currentUser?.full_name || '').split(/[\s@]/)[0];
  const errorsByAgent = useMemo(() => Object.fromEntries((overview?.agents_with_errors || []).map((a) => [a.agent_id, a.errors])), [overview]);
  const line = ovState === 'ready' ? statusLine(items, overview) : ovState === 'loading' ? 'Revisando la plataforma…' : ovState === 'missing' ? 'Falta activar el resumen de la plataforma (script 43).' : 'No se pudo revisar el estado de la plataforma.';
  const lineTone = ovState !== 'ready' ? 'bg-fg-faint' : items.some((i) => i.level === 'error') ? 'bg-signal' : items.some((i) => i.level === 'warn') ? 'bg-caution' : 'bg-live';

  // Respuestas de todos los agentes (motor y n8n) por día, con los días sin datos en cero.
  const series = useMemo(() => {
    const by = Object.fromEntries((overview?.messages?.series || []).map((r) => [r.date, Number(r.messages)]));
    return Array.from({ length: 30 }, (_, i) => { const d = isoDay(29 - i); return { date: d, messages: by[d] || 0 }; });
  }, [overview]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <section className="relative overflow-hidden rounded-[32px] bg-panel ring-1 ring-line p-6 sm:p-8" style={{ boxShadow: 'var(--shadow)' }}>
        <span aria-hidden className="pointer-events-none absolute -top-24 right-0 h-72 w-[28rem] rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(closest-side, #2BD9FE, #7C5CFF, transparent)' }} />
        <div className="relative flex flex-col lg:flex-row lg:items-end gap-6">
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-[34px] sm:text-[42px] leading-[1.05] font-bold text-fg">{greeting()}{firstName ? `, ${firstName}` : ''}.</h1>
            <p className="mt-2 flex items-center gap-2.5 text-[15px] text-fg-dim max-w-[60ch]" role="status">
              <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-full ${lineTone}`} />{line}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Link to="/agentes" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-fg text-ink text-[14px] font-semibold hover:bg-fg/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live focus-visible:ring-offset-2 focus-visible:ring-offset-panel">
              Ver agentes <FaArrowRight aria-hidden className="h-3 w-3" />
            </Link>
          </div>
        </div>
        {agents && agents.length > 0 && (
          <ul className="relative mt-7 flex gap-3 overflow-x-auto pb-1 [scrollbar-width:thin]" aria-label="Agentes de la plataforma">
            {agents.slice(0, 8).map((a) => {
              const look = agentLookOf(a);
              const on = a.status === 'published' && a.project?.engine === 'runtime';
              const errs = errorsByAgent[a.id] || 0;
              return (
                <li key={a.id} className="shrink-0">
                  <Link to={`/agentes/${a.id}`} className="flex w-36 flex-col items-center gap-2 rounded-2xl bg-raised/60 ring-1 ring-line px-3 py-4 text-center hover:ring-fg/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70 transition">
                    <AgentDot name={a.name} colors={look.colors} shape={look.avatar} imageUrl={look.avatarUrl} follow={look.follow} size={48} state={on && !errs ? 'idle' : 'sleep'} />
                    <span className="w-full truncate text-[13.5px] font-semibold text-fg">{a.name}</span>
                    {errs > 0 ? <StatusTag tone="caution" dot>{errs === 1 ? '1 error en 24 h' : `${errs} errores en 24 h`}</StatusTag>
                      : on ? <StatusTag tone="live" dot>Respondiendo</StatusTag> : <StatusTag>{a.status === 'published' ? 'Motor apagado' : 'Borrador'}</StatusTag>}
                  </Link>
                </li>
              );
            })}
            <li className="shrink-0">
              <Link to="/agentes" state={{ create: true }} className="flex h-full w-36 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-fg/20 px-3 py-4 text-[13.5px] font-semibold text-fg-dim hover:text-fg hover:border-fg/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-raised"><FaPlus aria-hidden /></span>Nuevo agente
              </Link>
            </li>
          </ul>
        )}
      </section>

      <AttentionCard items={items} generatedAt={overview?.generated_at} state={ovState} />

      {ovState === 'ready' && (
        <>
          <HealthCard engine={overview.engine24h} cost={overview.engine24h?.cost_usd} />

          <div className="grid gap-4 lg:grid-cols-3">
            <Card title="Respuestas de los agentes" className="lg:col-span-2"
              action={<p className="text-[12.5px] text-fg-faint">Hoy <b className="text-fg-dim tabular-nums">{fmt(overview.messages?.today)}</b> · ayer <b className="text-fg-dim tabular-nums">{fmt(overview.messages?.yesterday)}</b></p>}>
              <div className="text-fg-faint">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gMsg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={PALETTES.aurora.a} stopOpacity={0.35} /><stop offset="100%" stopColor={PALETTES.aurora.a} stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 6" stroke="currentColor" strokeOpacity={0.25} vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtShortDate} tick={{ fill: 'currentColor', fontSize: 11 }} axisLine={false} tickLine={false} interval={4} />
                    <YAxis tick={{ fill: 'currentColor', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} />
                    <Area type="monotone" dataKey="messages" name="Respuestas" stroke={PALETTES.aurora.a} strokeWidth={2.5} fill="url(#gMsg)" dot={false} activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-[12.5px] text-fg-faint">Los últimos 30 días, de todos los agentes: los de la plataforma y los externos que registran sus mensajes aquí.</p>
            </Card>
            <AgentsOrigin agents={overview.agents} projects={stats?.activeProjects} clients={stats?.totalClients} />
          </div>
        </>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Soporte" action={<Link to="/support" className="text-[13px] font-semibold text-fg-dim hover:text-fg rounded-full px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70">Ver tickets</Link>}>
          <ul className="space-y-2">
            {[['Abiertos', tickets.open, 'signal'], ['En progreso', tickets.inProgress, 'caution'], ['Cerrados', tickets.closed, 'live']].map(([label, n, tone]) => (
              <li key={label} className="flex items-center justify-between rounded-2xl bg-raised/60 px-4 h-14">
                <span className="flex items-center gap-2.5 text-[14px] text-fg-dim"><span className={`h-2 w-2 rounded-full ${{ signal: 'bg-signal', caution: 'bg-caution', live: 'bg-live' }[tone]} ${tone === 'signal' && n > 0 ? 'animate-pulse' : ''}`} aria-hidden />{label}</span>
                <span className="font-display text-[22px] font-bold tabular-nums text-fg">{fmt(n)}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Usuarios sobre su límite">
          {!stats?.overLimit?.length ? (
            <div className="flex flex-col items-center py-8 gap-2 text-center">
              <FaCheckCircle aria-hidden className="h-6 w-6 text-live" />
              <p className="text-[13.5px] text-fg-dim">Todos están dentro de su límite.</p>
            </div>
          ) : (
            <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {stats.overLimit.map((u) => (
                <li key={u.user_id} className="flex items-center gap-3 rounded-2xl bg-signal-soft px-4 py-2.5">
                  <FaExclamationTriangle aria-hidden className="h-3 w-3 shrink-0 text-signal" />
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-fg truncate">{u.email || u.user_id?.slice(0, 8)}</p>
                    <p className="text-[12px] text-fg-dim">Texto {fmt(u.text_used)}/{fmt(u.text_limit)} · Voz {fmt(u.voice_used)}/{fmt(u.voice_limit)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Actividad reciente">
          {log.length === 0 ? <p className="text-[13.5px] text-fg-dim">Sin actividad registrada.</p> : (
            <ol className="max-h-64 overflow-y-auto pr-1 divide-y divide-line">
              {log.map((e) => (
                <li key={e.id} className="flex flex-col gap-0.5 py-2.5 text-[13.5px]">
                  <p className="min-w-0 break-words text-fg-dim">
                    <span className="font-semibold text-fg">{e.performed_by_email || 'Admin'}</span> {ACTION_LABELS[e.action] || e.action}{e.target_email && e.target_email !== e.performed_by_email && <span className="text-fg"> {e.target_email}</span>}
                  </p>
                  <time className="text-[12px] text-fg-faint tabular-nums" dateTime={e.created_at}>{fmtDate(e.created_at)}</time>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
